import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, output } from '@angular/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DataTableComponent } from '@app/components/data-table/data-table.component';
import { ToastType } from '@app/enums/toast-type.enum';
import {
    customDateFormat,
    getDateFormat,
    getTimeZoneOffset,
} from '@app/helpers/date/date-helper';
import { formatString } from '@app/helpers/text/format-string';
import { ClientService } from '@app/modules/client-management/services/client/client.service';
import { IQrCodeDetail } from '@app/modules/client-management/types/qr-code-detail';
import { ToastService } from '@app/services/toast/toast.service';
import { AppStore } from '@app/stores/app/app.store';
import { CompanyStore } from '@app/stores/company/company.store';
import { ICustomErrorResponse } from '@app/types/custom-error-response';
import { IColumns, IData, ITableAction } from '@app/types/data-tables';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-qr-code-list',
    imports: [DataTableComponent],
    templateUrl: './qr-code-list.component.html',
    styleUrl: './qr-code-list.component.scss',
    providers: [
        DatePipe,
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: customDateFormat() },
    ],
})
export class QrCodeListComponent implements OnDestroy {
    ngOnDestroy(): void {
        this.subs$.unsubscribe();
    }
    private datePipe = inject(DatePipe);
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    private companyStore = inject(CompanyStore);
    private clientService = inject(ClientService);
    private toastService = inject(ToastService);
    subs$ = new Subscription();
    title = computed(() => {
        const { TITLE__QrCodeOnboarding } = this.translate();
        return TITLE__QrCodeOnboarding;
    });
    notFoundContent = computed(() => {
        const {
            ACTION__GenerateQRCode,
            MESSAGE__NoDataExists,
            NotFoundMessage,
            NoMatchesFound,
            MESSAGE__SearchNoQrResult,
        } = this.translate();
        return {
            btnText: ACTION__GenerateQRCode,
            baseTitle: MESSAGE__NoDataExists,
            baseDescription: NotFoundMessage,
            subTitle: NoMatchesFound,
            subDescription: MESSAGE__SearchNoQrResult,
            onClick: this.onAddQr.bind(this),
        };
    });

    dataColumns = computed(() => {
        const dateFormat = getDateFormat();
        const { Name, URL, CreatedOn, ExpiresOn, Status } = this.translate();
        return [
            {
                id: 1,
                dataIndex: 'name',
                title: Name,
                className: 'rcp-w-[240px]',
                sort: true,
            },
            {
                id: 2,
                dataIndex: 'qrLink',
                title: URL,
                sort: true,
                className: 'rcp-max-w-[400px]',
            },
            {
                id: 3,
                dataIndex: 'creationDate',
                title: CreatedOn,
                sort: true,
                date: dateFormat,
            },
            {
                id: 4,
                dataIndex: 'expirationDate',
                title: ExpiresOn,
                sort: true,
                date: dateFormat,
            },
            {
                id: 5,
                dataIndex: 'status',
                title: Status,
                sort: true,
            },
        ] as IColumns<{
            id: number;
            dataIndex: string;
            title: string;
            sort: boolean;
        }>[];
    });

    tableActions = computed(() => {
        const { ACTION__GenerateQRCode } = this.translate();
        const { maxActiveQRCodes, numberOfActiveQRCodes } =
            this.companyStore.clientDetailInfo().activeQRCodes;
        const disableAdd = maxActiveQRCodes <= numberOfActiveQRCodes;
        return {
            id: 1,
            label: ACTION__GenerateQRCode,
            testId: 'add',
            disabled: disableAdd,
            onClick: this.onAddQr.bind(this),
        };
    });

    searchNote = computed(() => {
        const { MaxQrCodesReached } = this.translate();
        const { maxActiveQRCodes, numberOfActiveQRCodes } =
            this.companyStore.clientDetailInfo().activeQRCodes;
        if (maxActiveQRCodes <= numberOfActiveQRCodes) {
            return formatString(MaxQrCodesReached, maxActiveQRCodes);
        }
        return '';
    });

    onStepChange = output();
    onAddQrClicked = output();

    onAddQr(callback: () => void) {
        callback();
        this.onAddQrClicked.emit();
    }
    actionTableAction(pagination: ITableAction<IQrCodeDetail>): void {
        this.subs$.add(
            this.clientService
                .getQrOnBoardingCodes(this.companyStore.id(), getTimeZoneOffset(), {
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    search: pagination.search,
                    sort: pagination.sort,
                    sortBy: pagination.sortBy,
                })
                .subscribe({
                    next: (res) => {
                        pagination.onFetch(res.items, res.totalPages, res.totalCount);
                    },
                    error: (errors: ICustomErrorResponse) => {
                        const messages = errors.messages;
                        messages.map((message) => {
                            this.toastService.addToast(ToastType.Error, message);
                        });
                        pagination.onFetch([], 0, 0);
                    },
                }),
        );
    }

    actionTableOnRowClick(row: IData<IQrCodeDetail>): void {
        this.companyStore.setQrDetails({
            value: {
                qrCodeId: row['qrCodeId'],
                name: row['name'],
                qrLink: row['qrLink'],
                expirationDate:
                    this.datePipe.transform(row['expirationDate'], 'yyyy-MM-dd') ?? '',
            },
        });
        this.companyStore.setGenerateQRDetails({
            value: {
                id: this.companyStore.id(),
                name: row['name'],
                expirationDate:
                    this.datePipe.transform(row['expirationDate'], 'yyyy-MM-dd') ?? '',
                description: '',
                onboarding: 0,
            },
        });
        this.onStepChange.emit();
    }
}
